import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import type { AppDispatch } from '@redux/store'

import {
  generateRedirectUrl, getQueryParamsFromHash, launchModal, parsePermittedQueryParams,
} from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'

import * as providerPackageActions from '@redux/modules/providerPackage'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import defaultFormState, { requiredFields } from '@models/providerPackage'
import type { ProviderModel, ProviderPackageModel, ProviderPackageRequestOptions } from '@models/types'

type GenerateUrlsOptions = {
  prefixRoute?: string,
}

export const generateUrls = (
  providerPackage: Partial<ProviderPackageModel> = {},
  options: GenerateUrlsOptions = {},
) => {
  const { id } = providerPackage
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    editProviderPackageUrl: `#/${prefixRoute}/providerPackages/${id}/edit`,
    hiddenProviderPackagesIndexUrl: `#/${prefixRoute}/providerPackages?hidden=true`,
    providerPackagesIndexUrl: `#/${prefixRoute}/providerPackages`,
  }
}

type CreateProviderPackageParams = {
  dispatch: AppDispatch,
  requestOptions?: ProviderPackageRequestOptions,
  providerPackageParams: Partial<ProviderPackageModel>,
}

const createProviderPackage = (params: CreateProviderPackageParams) => {
  const { providerPackageParams, dispatch, requestOptions } = params
  const { createProviderPackage: createFn } = providerPackageActions

  return dispatch(createFn(providerPackageParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editProviderPackageUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: `${editProviderPackageUrl}?redirect=${generateRedirectUrl()}`,
      }
    }

    return response
  })
}

type UpdateProviderPackageParams = {
  dispatch: AppDispatch,
  requestOptions?: ProviderPackageRequestOptions,
  providerPackage: ProviderPackageModel,
  providerPackageParams: Partial<ProviderPackageModel>,
}

const updateProviderPackage = (params: UpdateProviderPackageParams) => {
  const {
    providerPackage, providerPackageParams, dispatch, requestOptions,
  } = params
  const { updateProviderPackage: updateFn } = providerPackageActions

  const updatedParams = {
    id: providerPackage.id,
    ...providerPackageParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DuplicateProviderPackageParams = {
  dispatch: AppDispatch,
  requestOptions?: ProviderPackageRequestOptions,
  providerPackage: ProviderPackageModel,
  providerPackageParams: Partial<ProviderPackageModel>,
}

const duplicateProviderPackage = (params: DuplicateProviderPackageParams) => {
  const {
    providerPackage, providerPackageParams, dispatch, requestOptions,
  } = params
  const { duplicateProviderPackage: duplicateFn } = providerPackageActions

  const updatedParams = {
    id: providerPackage.id,
    ...providerPackageParams,
  }

  return dispatch(duplicateFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { id } = data

      const { editProviderPackageUrl } = generateUrls({ id })

      return {
        data,
        redirectUrl: editProviderPackageUrl,
        success,
      }
    }

    return response
  })
}

type DeleteProviderPackageParams = {
  dispatch: AppDispatch,
  requestOptions?: ProviderPackageRequestOptions,
  providerPackage: ProviderPackageModel,
}

const deleteProviderPackage = (params: DeleteProviderPackageParams) => {
  const { providerPackage, dispatch, requestOptions } = params
  const { deleteProviderPackage: deleteFn } = providerPackageActions

  // @ts-expect-error TS2554
  return dispatch(deleteFn(providerPackage, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { providerPackagesIndexUrl } = generateUrls()
      const { redirect }: { redirect?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['redirect'])

      return {
        ...response,
        redirectUrl: redirect || providerPackagesIndexUrl,
      }
    }

    return response
  })
}

type UnarchiveProviderPackageParams = {
  dispatch: AppDispatch,
  providerPackage: ProviderPackageModel,
  requestOptions?: ProviderPackageRequestOptions,
}

const unarchiveProviderPackage = (params: UnarchiveProviderPackageParams) => {
  const { dispatch, providerPackage, requestOptions } = params
  const { unarchiveProviderPackage: unarchiveFn } = providerPackageActions

  return dispatch(unarchiveFn(providerPackage, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useProviderPackageForm(
  providerPackage: Partial<ProviderPackageModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options

  const providerPackageForm = useForm(
    defaultFormState,
    { entity: providerPackage, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [providerPackage.id, providerPackage.cache_key],
  )

  return {
    ...providerPackageForm,
  }
}

export function useRelations(providerPackage: Partial<ProviderPackageModel>) {
  const { provider_id } = providerPackage || {}

  const providers = useSelector(reduxState => reduxState.entities.providers)

  // @ts-expect-error TS2538
  const provider: Partial<ProviderModel> = providers[provider_id] || {}

  return {
    provider,
  }
}

function useProviderPackage(initEntity: Partial<ProviderPackageModel> = {}) {
  const { entity: providerPackage } = useLatestEntity(initEntity, 'providerPackages')

  const dispatch = useDispatch()

  const {
    creating, deleting, loaded, loading, updating,
  } = useSelector(reduxState => reduxState.providerPackages)

  const { callbacks } = useContext(PageContext)

  const { provider } = useRelations(providerPackage)

  return {
    callbacks: {
      addProviderPackage: () => launchModal({
        callbacks,
        modalKey: 'CreateProviderPackageModal',
        payload: { providerPackage },
      }),
      createProviderPackage: (
        providerPackageParams: Partial<ProviderPackageModel>,
        requestOptions: ProviderPackageRequestOptions,
      ) => createProviderPackage({
        providerPackageParams, dispatch, requestOptions,
      }),
      duplicateProviderPackage: (
        providerPackageParams: Partial<ProviderPackageModel>,
        requestOptions: ProviderPackageRequestOptions,
      ) => duplicateProviderPackage({
        // @ts-expect-error TS2322
        providerPackage, providerPackageParams, dispatch, requestOptions,
      }),
      deleteProviderPackage: (requestOptions: ProviderPackageRequestOptions) => deleteProviderPackage({
        // @ts-expect-error TS2322
        providerPackage, dispatch, requestOptions,
      }),
      launchDuplicateProviderPackageModal: () => launchModal({
        callbacks,
        modalKey: 'DuplicateProviderPackageModal',
        payload: { providerPackage },
      }),
      unarchiveProviderPackage: (requestOptions: ProviderPackageRequestOptions) => (
        // @ts-expect-error TS2322
        unarchiveProviderPackage({ providerPackage, dispatch, requestOptions })
      ),
      updateProviderPackage: (
        providerPackageParams: Partial<ProviderPackageModel>,
        requestOptions: ProviderPackageRequestOptions,
      ) => updateProviderPackage({
        // @ts-expect-error TS2322
        providerPackage, providerPackageParams, dispatch, requestOptions,
      }),
    },
    creating,
    deleting,
    loaded,
    loading,
    provider,
    providerPackage,
    updating,
    urls: generateUrls(providerPackage),
  }
}

export type UseProviderPackagePayload = ReturnType<typeof useProviderPackage>

export default useProviderPackage
